import React, { useState } from 'react';
import Navigation from './Navigation';

const GlassNavDemo = () => {
  const [experimentalMode, setExperimentalMode] = useState(false);

  return (
    <div className="min-h-screen">
      {/* Navigation with glass effect */}
      <Navigation experimentalBlendMode={experimentalMode} />

      {/* Hero Section - Dark background */}
      <section 
        data-theme="dark" 
        className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black"
      >
        <div className="text-center px-6 max-w-4xl mx-auto">
          <h1 className="text-6xl md:text-8xl font-bold text-white mb-6">
            Glass Navigation
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Scroll to see the navigation adapt to different backgrounds
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => setExperimentalMode(!experimentalMode)}
              className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg backdrop-blur-sm border border-white/20 transition-all"
            >
              {experimentalMode ? 'Disable' : 'Enable'} Blend Mode
            </button>
          </div>
        </div>
      </section>

      {/* Light Section 1 - White background */}
      <section 
        data-theme="light" 
        className="min-h-screen flex items-center justify-center bg-white"
      >
        <div className="text-center px-6 max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6">
            Light Background
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            The navigation automatically switches to dark text and adjusted glass effects for optimal contrast on light backgrounds.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="p-6 bg-gray-50 rounded-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">WCAG AA Compliant</h3>
              <p className="text-gray-600">Maintains accessibility standards with proper contrast ratios</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Smooth Transitions</h3>
              <p className="text-gray-600">160ms transitions prevent jarring theme switches</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Liquid Glass</h3>
              <p className="text-gray-600">Backdrop blur and saturation create the glass effect</p>
            </div>
          </div>
        </div>
      </section>

      {/* Dark Section 2 - Gradient background */}
      <section 
        data-theme="dark" 
        className="min-h-screen flex items-center justify-center bg-gradient-to-r from-purple-900 via-blue-900 to-indigo-900"
      >
        <div className="text-center px-6 max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-7xl font-bold text-white mb-6">
            Dark Gradient
          </h2>
          <p className="text-xl text-purple-200 mb-8 max-w-2xl mx-auto">
            Back to light text with adjusted transparency for dark, colorful backgrounds. The glass effect adapts seamlessly.
          </p>
          <div className="mt-12 p-8 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10">
            <h3 className="text-2xl font-semibold text-white mb-4">Technical Implementation</h3>
            <ul className="text-left text-purple-100 space-y-2 max-w-2xl mx-auto">
              <li>• IntersectionObserver detects section boundaries</li>
              <li>• 1px probe positioned under navigation</li>
              <li>• CSS variables drive all color changes</li>
              <li>• No layout thrashing or performance issues</li>
              <li>• Graceful fallback for older browsers</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Light Section 2 - Gray background */}
      <section 
        data-theme="light" 
        className="min-h-screen flex items-center justify-center bg-gray-100"
      >
        <div className="text-center px-6 max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6">
            Subtle Gray
          </h2>
          <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            Even on subtle backgrounds, the navigation maintains perfect readability with its adaptive glass effect.
          </p>
          <div className="mt-12">
            <div className="inline-block p-8 bg-white rounded-2xl shadow-lg">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Color Values</h3>
              <div className="space-y-4 text-left">
                <div>
                  <h4 className="font-semibold text-gray-800">Dark Theme:</h4>
                  <code className="block mt-1 p-2 bg-gray-100 rounded text-sm">
                    bg: rgba(18, 18, 18, 0.35)<br/>
                    border: rgba(255, 255, 255, 0.12)<br/>
                    text: #f2f2f2
                  </code>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Light Theme:</h4>
                  <code className="block mt-1 p-2 bg-gray-100 rounded text-sm">
                    bg: rgba(255, 255, 255, 0.55)<br/>
                    border: rgba(0, 0, 0, 0.08)<br/>
                    text: #222222
                  </code>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dark Section 3 - Image background */}
      <section 
        data-theme="dark" 
        className="min-h-screen flex items-center justify-center relative"
        style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1557683316-973673baf926?w=1920&h=1080&fit=crop")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative text-center px-6 max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-7xl font-bold text-white mb-6">
            Image Backgrounds
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            The glass effect works beautifully over images, maintaining readability while showing the background through the blur.
          </p>
        </div>
      </section>

      {/* Light Section 3 - Colorful light background */}
      <section 
        data-theme="light" 
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-yellow-100 via-pink-100 to-purple-100"
      >
        <div className="text-center px-6 max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6">
            Colorful Light
          </h2>
          <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            Even on colorful light backgrounds, the adaptive system ensures perfect contrast and readability.
          </p>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {['bg-red-200', 'bg-blue-200', 'bg-green-200', 'bg-purple-200'].map((color, i) => (
              <div key={i} className={`h-32 ${color} rounded-lg flex items-center justify-center`}>
                <span className="text-gray-800 font-medium">Sample {i + 1}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Dark Section */}
      <section 
        data-theme="dark" 
        className="min-h-screen flex items-center justify-center bg-black"
      >
        <div className="text-center px-6 max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-7xl font-bold text-white mb-6">
            Pure Black
          </h2>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            The ultimate test - pure black background with the glass navigation maintaining its elegant appearance.
          </p>
          <div className="mt-12">
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="px-8 py-4 bg-white text-black rounded-lg font-semibold hover:bg-gray-200 transition-colors"
            >
              Back to Top
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GlassNavDemo;